import { useEffect, useRef, useState } from "react";
import { FaHistory, FaSearch } from "react-icons/fa";

import "./CfdHeaderSection.css";
import { didYouMeanCfdSuggestions, getCfdSearchUrl } from "@/api/cfd";
import PropertySuggestIcon from "../../../../../../public/assets/svg/faq/PropertySuggestIcon";
import PropertyRecentSearch from "../../../../../../public/assets/svg/faq/PropertyRecentSearch";
import { trackButtonClick } from "@/utils/analytics";

const CfdHeaderSection = ({
  setSearchUri,
  searchQuery,
  setSearchQuery,
  resetState,
  activeTab,
}) => {
  const searchTimeoutRef = useRef(null);
  const searchWrapperRef = useRef(null);
  const isAllTabFocused = activeTab === "all";

  const [isFocused, setIsFocused] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);

  // load recent searches from localstorage on mount
  useEffect(() => {
    if (!isAllTabFocused) {
      setSearchQuery("");
      setSearchUri("");
      setRecentSearches([]);
      setSuggestions([]);
      setShowSuggestions(false);
    }
    const saved = localStorage.getItem("cfdRecentSearches");
    if (suggestions.length > 0) {
      setSuggestions(suggestions);
      setShowSuggestions(true);
    } else {
      if (saved) {
        let savedSuggestions = JSON.parse(saved);

        setRecentSearches(savedSuggestions);
        setSuggestions(savedSuggestions);
        setShowSuggestions(true);
      }
    }
  }, [activeTab, isFocused, suggestions.length]);

  // Handle click outside to close the suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchWrapperRef.current &&
        !searchWrapperRef.current.contains(event.target)
      ) {
        setIsFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchCfdSearchUrl = async (suggestion) => {
    try {
      let result = await getCfdSearchUrl({
        locationId: suggestion.id,
        locationType: suggestion.type,
      });
      if (result.type === "VALID") {
        // Trigger search in parent component
        setSearchUri(result.searchURI);
      }
    } catch (error) {
      console.log("🚀 ~ fetchCfdSearchUrl ~ error:", error);
    }
  };

  const handleSuggestionClick = async (suggestion) => {
    // setSearchQuery(suggestion.label);
    setSearchQuery(
      suggestion.level1Text
        ? suggestion.level2Text
          ? `${suggestion.level1Text}, ${suggestion.level2Text}`
          : suggestion.level1Text
        : suggestion.label
    );
    setShowSuggestions(false);
    setIsFocused(false);

    // Save to recent searches
    const updatedSearches = [
      suggestion,
      ...recentSearches.filter((s) => s.id !== suggestion.id),
    ].slice(0, 10);

    setRecentSearches(updatedSearches);
    localStorage.setItem("cfdRecentSearches", JSON.stringify(updatedSearches));

    fetchCfdSearchUrl(suggestion);
  };

  const fetchSuggestions = async (query, isDirectSearch = false) => {
    try {
      const response = await didYouMeanCfdSuggestions(query);
      const suggestions = response?.suggestions || {};

      // Extract all suggestions from all categories
      const allSuggestions = [
        // Place suggestions
        ...(suggestions.Place?.STATE || []),
        ...(suggestions.Place?.CITY || []),
        ...(suggestions.Place?.ZIP || []),
        ...(suggestions.Place?.PLACE || []),
        ...(suggestions.Place?.NEIGHBORHOOD || []),
        ...(suggestions.Place?.COUNTY || []),
        ...(suggestions.Place?.MCDS || []),
        // Available Listings
        ...(suggestions["Available Listings"]?.MLS_ID || []),
        ...(suggestions["Available Listings"]?.PROPERTY_ID_ALIASES || []),
        ...(suggestions["Available Listings"]?.CFD_PROPERTY_ID || []),
        // Property Address
        ...(suggestions["Property Address"]?.ADDRESS || []),
        ...(suggestions["Property Address"]?.ADDRESS_CFD || []),
        // Recent and Saved searches
        ...(suggestions.RECENT_SEARCH?.RECENT_SEARCH || []),
        ...(suggestions.SAVE_SEARCH?.SAVE_SEARCH || []),
      ];

      if (allSuggestions.length === 0) {
        setSuggestions([]);
        setShowSuggestions(true);
        return;
      }

      setSuggestions(allSuggestions);
      setShowSuggestions(true);

      if (isDirectSearch && allSuggestions.length > 0) {
        handleSuggestionClick(allSuggestions[0]);
      }
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchUri("");
    setSearchQuery(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      if (!value.trim()) {
        setShowSuggestions(true);
        resetState("propertyType", []);
        resetState("occupancy", []);
        resetState("timeFrame", "");
      }

      if (value.trim()) {
        fetchSuggestions(value);
        trackButtonClick({
          eventName: "cfd_search_button_click",
          eventCategory: "my-shopper",
          eventSource: "on search click",
          eventLabel: `Search term: ${value} | type: Direct search`,
        });
      }
    }, 300);
  };

  return (
    <div className="cfd-header-section">
      <div className="cfd-header-content">
        <h2 className="cfd-header-title">Cash for Deed Opportunities</h2>
        {isAllTabFocused && (
          <div className="cfd-search-container" ref={searchWrapperRef}>
            <div className="cfd-search-input-wrapper">
              <input
                type="text"
                placeholder="Search CFDs by State, County, City, Zip Code..."
                value={searchQuery}
                onChange={(e) => handleInputChange(e)}
                onFocus={() => setIsFocused(true)}
                className="cfd-search-input"
              />
              <div
                className="cfd-search-icon-container"
                onClick={() => {
                  fetchSuggestions(searchQuery, true);
                  trackButtonClick({
                    eventName: "cfd_search_button_click",
                    eventCategory: "my-shopper",
                    eventSource: "on search click",
                    eventLabel: `Search term: ${searchQuery} | type: Direct search`,
                  });
                }}
              >
                <FaSearch className="cfd-search-icon" />
              </div>
            </div>

            {/* Suggestions dropdown */}
            {isFocused &&
              showSuggestions &&
              (searchQuery || recentSearches.length > 0) && (
                <div className="cfd-search-input-wrapper">
                  <div className="cfd-suggestions-dropdown">
                    {searchQuery ? (
                      suggestions.length > 0 ? (
                        suggestions.map((suggestion, index) => (
                          <div
                            key={index}
                            className="cfd-suggestion-item"
                            onClick={() => {
                              handleSuggestionClick(suggestion);
                              trackButtonClick({
                                eventName: "cfd_search_result_click",
                                eventCategory: "my-shopper",
                                eventSource: "on recent search click",
                                eventLabel: `Search term: ${suggestion.label} | type: Suggestion search`,
                              });
                            }}
                          >
                            <PropertySuggestIcon className="cfd-suggestion-icon" />
                            {/* <span>{suggestion.label}</span> */}
                            <span>
                              {suggestion.level1Text
                                ? suggestion.level2Text
                                  ? `${suggestion.level1Text}, ${suggestion.level2Text}`
                                  : suggestion.level1Text
                                : suggestion.label}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="no-suggestions">
                          No suggestions found
                        </div>
                      )
                    ) : (
                      recentSearches.map((recent, index) => (
                        <div
                          key={index}
                          className="cfd-suggestion-item recent"
                          onClick={() => {
                            handleSuggestionClick(recent);
                            trackButtonClick({
                              eventName: "cfd_search_result_click",
                              eventCategory: "my-shopper",
                              eventSource: "on recent search click",
                              eventLabel: `Search term: ${recent.label} | type: Recent search`,
                            });
                          }}
                        >
                          <PropertyRecentSearch className="cfd-suggestion-icon" />
                          {/* <span>{recent.label}</span> */}
                          <span>
                            {recent.level1Text
                              ? recent.level2Text
                                ? `${recent.level1Text}, ${recent.level2Text}`
                                : recent.level1Text
                              : recent.label}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  );
};
export default CfdHeaderSection;
